import { ASSESSMENT_EXPOSURE_POLICY } from '../constants';
import type { IAssessmentExposure } from '../types';

interface IFilterAssessmentCandidatesByExposureParams<T extends { id: string }> {
  candidates: readonly T[];
  currentAttemptItemIds: ReadonlySet<string>;
  exposures: readonly IAssessmentExposure[];
  now: number;
  plannedItemCount: number;
  requiredCandidateCount?: number;
}

export function filterAssessmentCandidatesByExposure<T extends { id: string }>({
  candidates,
  currentAttemptItemIds,
  exposures,
  now,
  plannedItemCount,
  requiredCandidateCount,
}: IFilterAssessmentCandidatesByExposureParams<T>): T[] {
  const latestExposureByItemId = new Map<string, number>();
  for (const exposure of exposures) {
    const previous = latestExposureByItemId.get(exposure.itemId) ?? Number.NEGATIVE_INFINITY;
    latestExposureByItemId.set(exposure.itemId, Math.max(previous, exposure.lastSeenAt));
  }
  const available = candidates.filter(({ id }) => !currentAttemptItemIds.has(id));
  const cutoff = now - ASSESSMENT_EXPOSURE_POLICY.windowMs;
  const fresh = available.filter(
    ({ id }) => (latestExposureByItemId.get(id) ?? cutoff - 1) < cutoff,
  );
  const recent = available
    .filter(({ id }) => (latestExposureByItemId.get(id) ?? cutoff - 1) >= cutoff)
    .sort((left, right) => {
      const exposureDifference =
        (latestExposureByItemId.get(left.id) ?? 0) - (latestExposureByItemId.get(right.id) ?? 0);
      return exposureDifference === 0 ? left.id.localeCompare(right.id) : exposureDifference;
    });
  const maximumOverlap = Math.floor(
    Math.max(0, plannedItemCount) * ASSESSMENT_EXPOSURE_POLICY.maximumOverlapRatio,
  );
  const usedRecentCount = [...currentAttemptItemIds].filter(
    (id) => (latestExposureByItemId.get(id) ?? cutoff - 1) >= cutoff,
  ).length;
  const remainingItemCount = Math.max(
    0,
    requiredCandidateCount ?? plannedItemCount - currentAttemptItemIds.size,
  );
  const remainingOverlap = Math.max(0, maximumOverlap - usedRecentCount);
  const missingCount = Math.max(0, remainingItemCount - fresh.length);
  return [...fresh, ...recent.slice(0, Math.min(remainingOverlap, missingCount))];
}
